import React, { useEffect, useRef } from 'react';

// ═══════════════════════════════════════════════════════════════
// SUPRALUMEN — HYPER-DIMENSIONAL ALPHA THETA FIELD
// R = α·π / (2·(−θ + 3π))
//
// 5D projection, Mandelbrot-AT at every frame, chromatic spirals,
// julia set variants, time breathing forward/backward, plasma blobs,
// user energy drives ALL parameters simultaneously.
// ═══════════════════════════════════════════════════════════════

const PHI  = (1 + Math.sqrt(5)) / 2;
const TAU  = Math.PI * 2;
const PHI2 = PHI * PHI;

const AlphaThetaCanvas = ({ fieldState, onBurst, mouseRef }) => {
  const canvasRef = useRef(null);
  const stateRef  = useRef({
    time:          0,
    timeSub:       0,          // secondary slower clock
    timeDirection: 1,
    timeDirSub:    1,          // independent second direction
    flipCooldown:  0,
    particles:     [],
    blobs:         [],
    juliaSeeds:    [],         // shifting julia set seed points
    userEnergy:    0,
    pulseEnergy:   0,          // extra spike on pulse reveal
    hueShift:      0,          // resets on mount; only drifts slowly
    animId:        null,
    W: 0, H: 0, cx: 0, cy: 0,
  });

  /* ── expose burst to parent ── */
  useEffect(() => {
    if (!onBurst) return;
    onBurst.current = (count) => {
      const s = stateRef.current;
      s.userEnergy  = Math.min(3.0 + count * 0.15, 6.0);
      s.pulseEnergy = count > 30 ? 3.5 : 0;          // big pulse on pulse-reveal
      s.hueShift    = (s.hueShift + 25) % 360;        // jump hue on each blessing
      burstParticles(s, 24 + count * 2, s.cx, s.cy);
      // flip time on every 3rd blessing — both clocks
      if (count % 3 === 0) s.timeDirection *= -1;
      if (count % 5 === 0) s.timeDirSub   *= -1;
      // reseed a julia point
      s.juliaSeeds[count % s.juliaSeeds.length] = {
        cr: (Math.random() - 0.5) * 0.8,
        ci: (Math.random() - 0.5) * 0.8,
      };
    };
  }, [onBurst]);

  /* ── main canvas setup & render loop ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const s      = stateRef.current;

    /* ── init blobs — 18 now, with 5D coords ── */
    s.blobs = [];
    for (let i = 0; i < 18; i++) {
      // First 10: gold family (38-58°), last 8: violet family (255-285°)
      // These NEVER leave their family via hueShift
      const isGold = i < 10;
      s.blobs.push({
        coords:   Array.from({ length: 5 }, () => (Math.random() - 0.5) * 2.4),
        velocity: Array.from({ length: 5 }, () => (Math.random() - 0.5) * 0.005),
        radius:   30 + Math.random() * 100,
        phase:    Math.random() * TAU,
        baseHue:  isGold ? 38 + Math.random() * 20 : 258 + Math.random() * 28,
        isGold,
      });
    }

    /* ── init julia seeds ── */
    s.juliaSeeds = Array.from({ length: 8 }, () => ({
      cr: (Math.random() - 0.5) * 1.2,
      ci: (Math.random() - 0.5) * 1.2,
    }));

    /* ── resize ── */
    function resize() {
      const dpr = window.devicePixelRatio || 1;
      s.W = window.innerWidth;
      s.H = window.innerHeight;
      canvas.width  = s.W * dpr;
      canvas.height = s.H * dpr;
      canvas.style.width  = s.W + 'px';
      canvas.style.height = s.H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      s.cx = s.W / 2;
      s.cy = s.H / 2;
    }
    window.addEventListener('resize', resize);
    resize();

    // Full black fill on mount — clears any stale canvas buffer from previous session
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.fillRect(0, 0, s.W, s.H);

    /* ── math helpers ── */

    // Core formula
    function AT(theta, alpha) {
      const d = 2 * (-theta + 3 * Math.PI);
      return Math.abs(d) < 0.01 ? 50 : (alpha * Math.PI) / d;
    }

    // Mandelbrot-AT (gold/violet blended)
    function mandelbrotAT(px, py, maxIter, alpha, tOff) {
      let zr = 0, zi = 0;
      for (let i = 0; i < maxIter; i++) {
        const mod   = Math.sqrt(zr * zr + zi * zi);
        const theta = (mod + tOff) % (3 * Math.PI * 0.95);
        const cR    = Math.max(-2.2, Math.min(2.2, AT(theta, alpha)));
        const nzr   = zr * zr - zi * zi + px * cR;
        const nzi   = 2 * zr * zi       + py * cR;
        zr = nzr; zi = nzi;
        if (zr * zr + zi * zi > 4) return i / maxIter;
      }
      return 1;
    }

    // Julia-AT variant — uses a shifting seed
    function juliaAT(px, py, cr, ci, maxIter, alpha, tOff) {
      let zr = px, zi = py;
      for (let i = 0; i < maxIter; i++) {
        const mod   = Math.sqrt(zr * zr + zi * zi);
        const theta = (mod + tOff * 0.7) % (3 * Math.PI * 0.95);
        const cR    = Math.max(-1.8, Math.min(1.8, AT(theta, alpha)));
        const nzr   = zr * zr - zi * zi + cr * cR;
        const nzi   = 2 * zr * zi       + ci * cR;
        zr = nzr; zi = nzi;
        if (zr * zr + zi * zi > 4) return i / maxIter;
      }
      return 1;
    }

    // 5D → 2D projection with user-reactive rotations
    function project5D(c, t) {
      const mx = mouseRef ? mouseRef.current.x : 0.5;
      const my = mouseRef ? mouseRef.current.y : 0.5;
      let [x, y, z, w, v] = c;

      // XY  (standard)
      const a1  = t * 0.13 + s.hueShift * 0.002;
      const x1  = x * Math.cos(a1) - y * Math.sin(a1);
      const y1  = x * Math.sin(a1) + y * Math.cos(a1);

      // XZ  (time dimension — direction flips)
      const a2  = t * 0.07 * s.timeDirection + s.timeSub * 0.04;
      const x2  = x1 * Math.cos(a2) - z  * Math.sin(a2);
      const z1  = x1 * Math.sin(a2) + z  * Math.cos(a2);

      // YW  (tangential)
      const a3  = t * 0.11 + s.timeSub * 0.06 * s.timeDirSub;
      const y2  = y1 * Math.cos(a3) - w  * Math.sin(a3);
      const w1  = y1 * Math.sin(a3) + w  * Math.cos(a3);

      // ZW  (hyper — mouse-reactive)
      const a4  = t * 0.05 + mx * 1.2 + s.userEnergy * 0.12;
      const z2  = z1 * Math.cos(a4) - w1 * Math.sin(a4);
      const w2  = z1 * Math.sin(a4) + w1 * Math.cos(a4);

      // XV  (5th axis — secondary clock driven)
      const a5  = s.timeSub * 0.09 * s.timeDirSub + my * 0.8;
      const x3  = x2 * Math.cos(a5) - v  * Math.sin(a5);
      const fz  = z2 * Math.cos(a5 * 0.3) - w2 * Math.sin(a5 * 0.3);

      // Perspective
      const depth = 4 / (4 + fz * 0.35);
      return { x: x3 * depth, y: y2 * depth, depth: fz, scale: depth };
    }

    // Smooth bezier curve
    function drawCurve(pts) {
      if (pts.length < 3) return;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length - 1; i++) {
        const xc = (pts[i][0] + pts[i + 1][0]) / 2;
        const yc = (pts[i][1] + pts[i + 1][1]) / 2;
        ctx.quadraticCurveTo(pts[i][0], pts[i][1], xc, yc);
      }
      ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
    }

    /* ─────────────────────────────────────────────────────────── */
    /* MAIN RENDER LOOP                                            */
    /* ─────────────────────────────────────────────────────────── */
    function drawFrame() {
      /* ── clocks ── */
      const ue      = s.userEnergy;
      const speed   = 0.0008 + ue * 0.0004;           // faster under user energy
      s.time       += speed * s.timeDirection;
      s.timeSub    += (speed * 0.37) * s.timeDirSub;
      s.userEnergy  = Math.max(0, s.userEnergy * 0.994);
      s.pulseEnergy = Math.max(0, s.pulseEnergy * 0.988);
      s.hueShift    = (s.hueShift + 0.03) % 360;     // very slow ambient hue drift

      /* ── time direction oscillation (two independent clocks) ── */
      if (s.flipCooldown > 0) {
        s.flipCooldown--;
      } else {
        if (Math.sin(s.time * 0.3) > 0.95)  { s.timeDirection = -1; s.flipCooldown = 40; }
        if (Math.sin(s.time * 0.3) < -0.95) { s.timeDirection =  1; s.flipCooldown = 40; }
        if (Math.sin(s.timeSub * 0.5) > 0.92)  s.timeDirSub = -1;
        if (Math.sin(s.timeSub * 0.5) < -0.92) s.timeDirSub =  1;
      }

      const t       = s.time;
      const ts      = s.timeSub;
      const { W, H, cx, cy, blobs } = s;
      const baseR   = Math.min(W, H) * 0.38;

      const fs      = fieldState.current || { count: 0, alpha: 1.0, theta: 0, R: 0.167 };
      const count   = fs.count || 0;
      const alpha   = fs.alpha || 1.0;

      /* ── trail fade — faster base fade to prevent color buildup ── */
      const trailFade = Math.max(0.025, 0.048 - ue * 0.005 - count * 0.0005);
      ctx.fillStyle = `rgba(0,0,0,${trailFade})`;
      ctx.fillRect(0, 0, W, H);

      /* ── background pulse ── */
      const bgP   = Math.sin(t * 0.12) * 0.5 + 0.5;
      const bgP2  = Math.sin(ts * 0.19 + 1.3) * 0.5 + 0.5;
      // hue breathes slowly between warm gold (38°) and violet (268°)
      const hueA  = 38  + Math.sin(t * 0.04 + ts * 0.03) * 14; // 24-52°
      const hueB  = 268 + Math.sin(ts * 0.05 + t * 0.02) * 18; // 250-286°
      const bgGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, baseR * 3.2);
      bgGrd.addColorStop(0,   `hsla(${hueA},78%,50%,${0.014 + bgP * 0.009})`);
      bgGrd.addColorStop(0.2, `hsla(${hueB},63%,40%,${0.007 + bgP2 * 0.005})`);
      bgGrd.addColorStop(0.6, `hsla(${(hueA + 60) % 360},42%,26%,0.003)`);
      bgGrd.addColorStop(1,   'rgba(0,0,0,0)');
      ctx.fillStyle = bgGrd;
      ctx.fillRect(0, 0, W, H);

      /* ══════════════════════════════════════════════════════════
         LAYER 1 — 5D PLASMA BLOBS
         ══════════════════════════════════════════════════════════ */
      const mx0 = mouseRef ? mouseRef.current.x : 0.5;
      const my0 = mouseRef ? mouseRef.current.y : 0.5;

      blobs.forEach((blob, bi) => {
        const bTheta = (t * 0.15 + blob.phase + ts * 0.07) % (3 * Math.PI * 0.95);
        const R      = AT(bTheta, alpha + bi * 0.025);
        const cR     = Math.max(-2, Math.min(2, R));

        // update 5D coordinates
        for (let d = 0; d < 5; d++) {
          const rate = 0.1 + d * 0.06;
          blob.velocity[d] += Math.sin(t * rate + blob.phase + d * 1.3) * 0.00035 * cR;
          blob.velocity[d] += Math.cos(ts * (rate * 0.7) + d)           * 0.00020 * cR;
          blob.velocity[d] += (mx0 - 0.5) * 0.00008 * (d < 3 ?  1 : -1);
          blob.velocity[d] += (my0 - 0.5) * 0.00008 * (d % 2 === 0 ? 1 : -1);
          blob.velocity[d] += ue * 0.00008 * Math.sin(t * 0.5 + d);  // user energy kick
          blob.velocity[d] *= 0.998;
          blob.coords[d]   += blob.velocity[d];
          if (Math.abs(blob.coords[d]) > 2.2) blob.velocity[d] *= -0.6;
        }

        const proj    = project5D(blob.coords, t);
        const sx      = cx + proj.x * baseR * 1.4;
        const sy      = cy + proj.y * baseR * 1.4;
        const breathe = 1 + Math.sin(t * 0.2 + ts * 0.13 + blob.phase) * 0.22;
        const r       = (blob.radius * breathe + count * 1.2 + ue * 8) * proj.scale;
        // hue strictly clamped to gold (20-65°) or violet (235-305°) families
        const hueExcursion = Math.sin(t * 0.025 + bi * 0.7) * 22;
        const hue = blob.isGold
          ? Math.max(28, Math.min(55, blob.baseHue + hueExcursion))
          : Math.max(240, Math.min(300, blob.baseHue + hueExcursion));
        const inner   = 0.04 + bgP * 0.02 + ue * 0.015 + count * 0.002;

        if (r < 1) return;
        const grd = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
        grd.addColorStop(0,   `hsla(${hue},85%,62%,${inner * 1.8})`);
        grd.addColorStop(0.3, `hsla(${(hue+25)%360 < 90 || (hue+25)%360 > 200 ? hue+25 : hue},72%,48%,${inner})`);
        grd.addColorStop(0.65, `hsla(${hue},60%,35%,${inner * 0.5})`);
        grd.addColorStop(1,    `hsla(${hue},45%,18%,0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, TAU);
        ctx.fill();
      });

      /* ══════════════════════════════════════════════════════════
         LAYER 2 — MANDELBROT-AT FRACTAL FIELD
         (always on, intensity grows with blessings + user energy)
         ══════════════════════════════════════════════════════════ */
      {
        const res     = count > 5 ? 5 : 6;             // higher res with more blessings
        const maxIter = Math.min(8 + count + Math.floor(ue * 3), 35);
        const zoom    = 2.8 - Math.sin(t * 0.1) * 0.7 - Math.sin(ts * 0.17) * 0.4;
        const panX    = Math.sin(t * 0.07) * 0.35 + Math.sin(ts * 0.13) * 0.2 + (mx0 - 0.5) * 0.5;
        const panY    = Math.cos(t * 0.09) * 0.35 + Math.cos(ts * 0.11) * 0.2 + (my0 - 0.5) * 0.5;
        const tOff    = t * 2.2 + ts * 0.8;
        const fracW   = Math.min(W, 700);
        const fracH   = Math.min(H, 700);
        const sx0     = cx - fracW / 2;
        const sy0     = cy - fracH / 2;
        const baseOp  = 0.012 + count * 0.003 + ue * 0.008;

        for (let px = 0; px < fracW; px += res) {
          for (let py = 0; py < fracH; py += res) {
            const mpx = (px / fracW - 0.5) * zoom + panX;
            const mpy = (py / fracH - 0.5) * zoom + panY;
            const ft  = mandelbrotAT(mpx, mpy, maxIter, alpha, tOff);

            if (ft > 0 && ft < 0.98) {
              // chromatic aberration: draw 3 offset passes (R, G, B channels)
              // gold to amber to violet sweep — skip the green zone
              const hue1 = ft < 0.5
                ? 38 + ft * 2 * 22          // 38→60° (gold to amber)  
                : 255 + (ft - 0.5) * 2 * 30; // 255→285° (violet to indigo)
              const hue2 = ft < 0.5
                ? 255 + ft * 2 * 15         // 255→270° (violet)
                : 38 + (ft - 0.5) * 2 * 15; // 38→53° (gold)
              const a    = baseOp * (0.5 + ft * 0.9);

              const dim  = project5D(
                [mpx * 0.5, mpy * 0.5, Math.sin(ft * TAU) * 0.5, Math.cos(ft * TAU) * 0.5, Math.sin(tOff * 0.3) * 0.3],
                t * 0.5
              );

              ctx.fillStyle = `hsla(${hue1},85%,60%,${(a * dim.scale).toFixed(4)})`;
              ctx.fillRect(sx0 + px,     sy0 + py,     res, res);
              ctx.fillStyle = `hsla(${hue2},75%,50%,${(a * dim.scale * 0.5).toFixed(4)})`;
              ctx.fillRect(sx0 + px + 1, sy0 + py + 1, res, res);
            }
          }
        }
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 2b — JULIA-AT VARIANTS (appear after 3 blessings)
         Multiple shifting seeds create evolving julia structures
         ══════════════════════════════════════════════════════════ */
      if (count >= 3) {
        const seeds   = s.juliaSeeds;
        const numSeeds = Math.min(Math.floor(count / 2) + 1, seeds.length);
        const jRes    = count > 8 ? 7 : 9;
        const jMaxI   = Math.min(10 + count, 25);
        const jZoom   = 2.2 - Math.sin(ts * 0.14) * 0.6;
        const jTOff   = ts * 1.8 + t * 0.5;

        for (let si = 0; si < numSeeds; si++) {
          const seed = seeds[si];
          // seeds drift over time
          seed.cr += Math.sin(t * 0.02 + si * PHI) * 0.0004 * s.timeDirection;
          seed.ci += Math.cos(ts * 0.03 + si * PHI2) * 0.0004 * s.timeDirSub;
          seed.cr = Math.max(-0.9, Math.min(0.9, seed.cr));
          seed.ci = Math.max(-0.9, Math.min(0.9, seed.ci));

          const jW  = Math.min(W, 500);
          const jH  = Math.min(H, 500);
          const jx0 = cx - jW / 2;
          const jy0 = cy - jH / 2;
          const jOp = 0.005 + ue * 0.003 + si * 0.001;

          for (let px = 0; px < jW; px += jRes) {
            for (let py = 0; py < jH; py += jRes) {
              const mpx = (px / jW - 0.5) * jZoom;
              const mpy = (py / jH - 0.5) * jZoom;
              const jt  = juliaAT(mpx, mpy, seed.cr, seed.ci, jMaxI, alpha, jTOff);

              if (jt > 0 && jt < 0.95) {
                // violet-gold only, no green
                const hue = si % 2 === 0
                  ? 258 + si * 8 + jt * 25  // violet family
                  : 38  + si * 5 + jt * 20; // gold family
                const a   = jOp * (0.3 + jt);
                ctx.fillStyle = `hsla(${hue},70%,55%,${a.toFixed(4)})`;
                ctx.fillRect(jx0 + px, jy0 + py, jRes, jRes);
              }
            }
          }
        }
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 3 — HYPER-SPIRAL LAYERS (5D projection)
         More base layers, hue-shifted, all axis involved
         ══════════════════════════════════════════════════════════ */
      {
        const layers  = Math.min(Math.floor(count / 1.5) + 6, 20);
        for (let layer = 0; layer < layers; layer++) {
          const lPhase = t  * (0.055 + layer * 0.009) * s.timeDirection
                       + ts * (0.033 + layer * 0.005) * s.timeDirSub
                       + layer * PHI;
          const lAlpha = alpha + layer * 0.06;
          const pts    = [];

          for (let i = 0; i < 200; i++) {
            const lt    = (i / 200) * TAU * 2.5;
            const theta = (lt + lPhase) % (3 * Math.PI * 0.96);
            const R     = AT(theta, lAlpha);
            const cR    = Math.max(-3.5, Math.min(3.5, R));
            const gAngle = i * 2.39996323 + lPhase;
            // 5th dimension driven by secondary clock
            const sR    = baseR * (0.04 + Math.abs(cR) * 0.09)
                        * (1 + 0.12 * Math.sin(t * 0.15 + layer)
                             + 0.06 * Math.sin(ts * 0.23 + layer * PHI));

            const c5 = [
              Math.cos(gAngle) * sR / baseR,
              Math.sin(gAngle) * sR / baseR,
              Math.sin(lt * 1.5 + t * 0.3) * 0.55,
              Math.cos(lt + ts * 0.25) * 0.35,
              Math.sin(lt * 0.7 + ts * 0.18 + layer) * 0.25,  // 5th axis
            ];

            const proj = project5D(c5, t + layer * 0.12);
            pts.push([cx + proj.x * baseR, cy + proj.y * baseR]);
          }

          drawCurve(pts);
          // alternates between gold family and violet family per layer, no green zone
          const phase = Math.sin(t * 0.2 + ts * 0.13 + layer * 0.4) * 0.5 + 0.5;
          const hue   = layer % 2 === 0
            ? 38  + phase * 20   // 38-58° gold/amber
            : 255 + phase * 28;  // 255-283° violet/indigo
          ctx.strokeStyle = `hsla(${hue},78%,62%,${0.018 + 0.055 / (layer + 1)})`;
          ctx.lineWidth   = 0.5 + 0.9 / (layer + 1);
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.stroke();
        }
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 4 — DIMENSIONAL BREATHING RINGS (7 rings, 5D tilt)
         ══════════════════════════════════════════════════════════ */
      for (let ring = 0; ring < 7; ring++) {
        const rTheta = (t * 0.22 + ts * 0.15 + ring * 0.32) % (3 * Math.PI * 0.95);
        const rR     = AT(rTheta, alpha);
        const cRR    = Math.max(0.08, Math.min(2.8, Math.abs(rR)));
        const tilt1  = Math.sin(t * 0.08 + ts * 0.06 + ring * 0.7) * 0.4;
        const tilt2  = Math.cos(ts * 0.1 + ring * 0.5) * 0.25;
        const rPts   = [];
        const rRad   = baseR * (0.12 + ring * 0.12) * cRR * 0.42;

        for (let i = 0; i <= 72; i++) {
          const angle = (i / 72) * TAU;
          const rx    = Math.cos(angle) * rRad;
          const ry    = Math.sin(angle) * rRad * Math.cos(tilt1);
          const rz    = Math.sin(angle) * rRad * Math.sin(tilt1);
          const rw    = Math.cos(angle * 1.5 + ts * 0.1) * rRad * 0.25 * Math.sin(tilt2);
          const rv    = Math.sin(angle * 0.7 + t * 0.07) * rRad * 0.15;
          const proj  = project5D([rx / baseR, ry / baseR, rz / baseR, rw / baseR, rv / baseR], t);
          rPts.push([cx + proj.x * baseR, cy + proj.y * baseR]);
        }

        ctx.beginPath();
        ctx.moveTo(rPts[0][0], rPts[0][1]);
        for (let i = 1; i < rPts.length; i++) ctx.lineTo(rPts[i][0], rPts[i][1]);
        const rHue = ring < 4 ? 44 + ring * 12 : 268 + (ring - 4) * 10;
        ctx.strokeStyle = `hsla(${rHue},65%,55%,${0.015 - ring * 0.0015})`;
        ctx.lineWidth   = 0.35;
        ctx.stroke();
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 5 — CENTRAL HYPERSPHERE NODE
         Pulses on both clocks, rings + core + halo
         ══════════════════════════════════════════════════════════ */
      {
        const osc   = Math.sin(t * 0.3) * 0.5 + 0.5;
        const osc2  = Math.sin(ts * 0.5 + 1.2) * 0.5 + 0.5;
        const cRad  = 14 + osc * 12 + osc2 * 8 + count * 0.4 + ue * 4 + s.pulseEnergy * 6;

        // Glow stack (6 layers)
        for (let g = 5; g >= 0; g--) {
          const gR  = cRad * (1 + g * 0.7);
          const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, gR);
          const h1  = 44;   // always gold
          const h2  = 268;  // always violet
          grd.addColorStop(0,   `hsla(${h1},92%,68%,${0.06 / (g + 1)})`);
          grd.addColorStop(0.4, `hsla(${h1},78%,52%,${0.028 / (g + 1)})`);
          grd.addColorStop(0.7, `hsla(${h2},68%,42%,${0.012 / (g + 1)})`);
          grd.addColorStop(1,   'rgba(0,0,0,0)');
          ctx.fillStyle = grd;
          ctx.beginPath();
          ctx.arc(cx, cy, gR, 0, TAU);
          ctx.fill();
        }
        // Core dot
        ctx.beginPath();
        ctx.arc(cx, cy, 2.5 + osc * 1.5 + s.pulseEnergy * 0.8, 0, TAU);
        ctx.fillStyle = `rgba(240,216,112,${0.55 + osc * 0.35})`; // always bright gold core
        ctx.fill();
        // Halo
        ctx.beginPath();
        ctx.arc(cx, cy, 7 + osc * 3, 0, TAU);
        ctx.strokeStyle = `hsla(${(44 + s.hueShift * 0.2) % 360},85%,60%,${0.12 + osc * 0.06})`;
        ctx.lineWidth = 0.4;
        ctx.stroke();
        // Secondary halo (osc2 phase)
        ctx.beginPath();
        ctx.arc(cx, cy, 12 + osc2 * 5 + ue * 1.5, 0, TAU);
        ctx.strokeStyle = `hsla(${(270 + s.hueShift * 0.3) % 360},70%,55%,${0.06 + osc2 * 0.03})`;
        ctx.lineWidth = 0.25;
        ctx.stroke();
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 6 — PHI ORBITAL NODES (5D orbits)
         More nodes, more axes, hue-shifted per node
         ══════════════════════════════════════════════════════════ */
      {
        const orbs  = Math.max(Math.min(count + 4, 32), 6);
        for (let i = 0; i < orbs; i++) {
          const ang   = (i / orbs) * TAU + t * 0.03 + ts * 0.019 + i * PHI * 0.08;
          const oR    = 0.12 + (i / orbs) * 0.6;
          const pulse = Math.sin(t * 0.4 + ts * 0.27 + i * PHI) * 0.5 + 0.5;
          const nc    = [
            Math.cos(ang)           * oR,
            Math.sin(ang)           * oR,
            Math.sin(ang * 2 + t * 0.1 + ts * 0.07) * 0.22,
            Math.cos(ang * 0.5 + ts * 0.15)          * 0.18,
            Math.sin(ang * 0.3 + t  * 0.08 + i)      * 0.12,
          ];
          const proj  = project5D(nc, t);
          const ox    = cx + proj.x * baseR;
          const oy    = cy + proj.y * baseR;
          const hue   = i < orbs / 2
            ? 44 + Math.sin(t * 0.04 + i) * 15   // gold family: 29-59°
            : 268 + Math.sin(ts * 0.05 + i) * 18; // violet family: 250-286°

          const ngrd  = ctx.createRadialGradient(ox, oy, 0, ox, oy, 7 * proj.scale);
          ngrd.addColorStop(0, `hsla(${hue},88%,68%,${(0.14 + pulse * 0.1) * proj.scale})`);
          ngrd.addColorStop(1, `hsla(${hue},62%,42%,0)`);
          ctx.fillStyle = ngrd;
          ctx.beginPath();
          ctx.arc(ox, oy, 6 * proj.scale, 0, TAU);
          ctx.fill();

          ctx.beginPath();
          ctx.arc(ox, oy, (0.8 + pulse * 0.5) * proj.scale, 0, TAU);
          ctx.fillStyle = `hsla(${(hue + 30) % 360},95%,80%,${(0.25 + pulse * 0.15) * proj.scale})`;
          ctx.fill();
        }
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 7 — FRACTAL BRANCHES (5D, more branches)
         ══════════════════════════════════════════════════════════ */
      if (count > 0) {
        const branches = Math.min(count + 2, 16);
        for (let b = 0; b < branches; b++) {
          const bAng  = (b / branches) * TAU + t * 0.022 * s.timeDirection + ts * 0.014 * s.timeDirSub;
          const steps = 50 + count * 2;
          const pts   = [];

          for (let step = 0; step < steps; step++) {
            const ft     = step / steps;
            const theta  = ft * TAU * 2.5 + bAng;
            const R      = AT(theta % (3 * Math.PI * 0.95), alpha);
            const cR     = Math.max(-1.8, Math.min(1.8, R));
            const sAng   = bAng + ft * TAU * PHI;
            const dist   = ft * (0.32 + Math.abs(cR) * 0.06 + ue * 0.02);
            const bc = [
              Math.cos(sAng) * dist,
              Math.sin(sAng) * dist,
              Math.sin(sAng * 0.5 + t  * 0.2) * ft * 0.35,
              Math.cos(sAng * 0.3 + ts * 0.18) * ft * 0.25,
              Math.sin(sAng * 0.7 + t  * 0.12 + b) * ft * 0.18,
            ];
            const proj = project5D(bc, t + b * 0.06);
            pts.push([cx + proj.x * baseR, cy + proj.y * baseR]);
          }

          drawCurve(pts);
          const bHue = b % 2 === 0
            ? 44 + Math.sin(t * 0.05 + b) * 14  // gold: 30-58°
            : 265 + Math.sin(ts * 0.06 + b) * 16; // violet: 249-281°
          ctx.strokeStyle = `hsla(${bHue},70%,55%,${0.01 + 0.006 / (b + 1)})`;
          ctx.lineWidth   = 0.28;
          ctx.lineJoin    = 'round';
          ctx.lineCap     = 'round';
          ctx.stroke();
        }
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 8 — PARTICLES
         ══════════════════════════════════════════════════════════ */
      s.particles = s.particles.filter(p => p.life > 0);
      for (const p of s.particles) {
        p.x    += p.vx;
        p.y    += p.vy;
        p.vx   *= 0.997;
        p.vy   *= 0.997;
        p.life -= p.decay;
        if (p.life <= 0) continue;

        const a   = p.life * 0.42;
        const hue = (p.hue + s.hueShift * 0.3) % 360;
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * p.life * 4);
        grd.addColorStop(0, `hsla(${hue},90%,70%,${a.toFixed(3)})`);
        grd.addColorStop(1, `hsla(${hue},60%,40%,0)`);
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.life * 4, 0, TAU);
        ctx.fill();
      }

      /* ══════════════════════════════════════════════════════════
         LAYER 9 — CHROMATIC PULSE RING (on pulse reveal)
         ══════════════════════════════════════════════════════════ */
      if (s.pulseEnergy > 0.1) {
        const pe  = s.pulseEnergy;
        const pR  = baseR * (0.3 + pe * 0.4);
        for (let ring = 0; ring < 3; ring++) {
          const rOff = ring * 0.4;
          ctx.beginPath();
          ctx.arc(cx, cy, pR * (1 + ring * 0.35), 0, TAU);
          ctx.strokeStyle = `hsla(${(44 + ring * 120 + s.hueShift) % 360},85%,65%,${(pe * 0.18 / (ring + 1)).toFixed(3)})`;
          ctx.lineWidth   = 1.5 - ring * 0.4;
          ctx.stroke();
        }
      }

      s.animId = requestAnimationFrame(drawFrame);
    }

    s.animId = requestAnimationFrame(drawFrame);

    return () => {
      if (s.animId) cancelAnimationFrame(s.animId);
      window.removeEventListener('resize', resize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      id="alphaTheta"
      data-testid="field-canvas"
      aria-label="Alpha Theta fractal field visualization"
    />
  );
};

/* ── particle burst helper ── */
function burstParticles(s, count, cx, cy) {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * TAU;
    const speed = 0.4 + Math.random() * 1.8;
    const hue   = i < count * 0.65
      ? 38  + Math.random() * 25   // gold/amber range
      : 255 + Math.random() * 40;  // violet/indigo range
    s.particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life:  1.0,
      decay: 0.0015 + Math.random() * 0.003,
      size:  1.2 + Math.random() * 3,
      hue,
    });
  }
}

export default AlphaThetaCanvas;
